import React, { createContext, useContext, useState, useCallback } from 'react';
import { Fragment, useEffect } from "react";
import { createPortal } from "react-dom";
import { Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

type NotificationType = "success" | "error" | "info";

interface NotificationData {
  title: string;
  description: string;
  type: NotificationType;
}

interface NotificationContextType {
  showNotification: (data: NotificationData) => void;
  success: (title: string, description: string) => void;
  error: (title: string, description: string) => void;
  info: (title: string, description: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [show, setShow] = useState(false);
  const [data, setData] = useState<NotificationData>({
    title: '',
    description: '',
    type: 'success'
  });

  useEffect(() => {
    if (show) {
      const timeoutId = setTimeout(() => {
        setShow(false);
      }, 5000);
      return () => clearTimeout(timeoutId);
    }
  }, [show]);

  const showNotification = useCallback((notificationData: NotificationData) => {
    setData(notificationData);
    setShow(true);
  }, []);

  const success = useCallback((title: string, description: string) => {
    showNotification({ title, description, type: 'success' });
  }, [showNotification]);

  const error = useCallback((title: string, description: string) => {
    showNotification({ title, description, type: 'error' });
  }, [showNotification]);

  const info = useCallback((title: string, description: string) => {
    showNotification({ title, description, type: 'info' });
  }, [showNotification]);

  const notificationContent = (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 flex px-4 py-6 sm:items-start sm:p-6 z-[999]"
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        <Transition
          show={show}
          as={Fragment}
          enter="transform ease-out duration-300 transition"
          enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
          enterTo="translate-y-0 opacity-100 sm:translate-x-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {data.type === "success" ? (
                    <CheckCircleIcon
                      className="h-6 w-6 text-green-400"
                      aria-hidden="true"
                    />
                  ) : data.type === "error" ? (
                    <ExclamationCircleIcon
                      className="h-6 w-6 text-red-400"
                      aria-hidden="true"
                    />
                  ) : (
                    <InformationCircleIcon
                      className="h-6 w-6 text-blue-400"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className="text-sm font-medium text-gray-900">{data.title}</p>
                  <p className="mt-1 text-sm text-gray-500">{data.description}</p>
                </div>
                <div className="ml-4 flex flex-shrink-0">
                  <button
                    type="button"
                    className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={() => setShow(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  );

  return (
    <NotificationContext.Provider value={{ showNotification, success, error, info }}>
      {children}
      {createPortal(notificationContent, document.getElementById("root") as HTMLElement)}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};
